import { useState, useEffect, useRef } from 'react'
import PropTypes from 'prop-types'
import Highcharts from 'highcharts'
import 'highcharts/modules/no-data-to-display'
import { useTranslation } from 'react-i18next'

import StyledHighcharts, { tooltipStyle } from './StyledHighcharts'
import { HIGHCHART_SERIES_COLORS } from 'constants/colorMaps'

const pieInnerSize = 0.9

const DonutChart = ({ data }) => {
  const { t } = useTranslation()
  const chartRef = useRef(null)
  const labelsGroupSVG = useRef(null)
  const noDataText = t('common.noChartData')

  const [chartOptions, setChartOptions] = useState({
    chart: {
      type: 'pie',
      spacing: [0, 0, 14, 0],
      events: {
        redraw: () => { redrawCallback() },
      },
    },

    lang: {
      noData:
        '<div style="text-align: center; position: relative; margin-top: 35px;">' +
          '<i class="icon-chart-nodata -nohover" style="margin-bottom: 16px;"></i><br>' +
          noDataText +
        '</div>',
    },
    noData: {
      style: {
        fontWeight: 'normal',
        fontSize: '12px',
        fontFamily: 'Open Sans',
      },
      useHTML: true,
    },

    tooltip: {
      ...tooltipStyle,
      headerFormat: '<span style="color:{point.color}">\u25CF</span> {point.key}: {point.y}',
      pointFormat: '',
      outside: true,
    },

    plotOptions: {
      'pie': {
        colors: HIGHCHART_SERIES_COLORS,
        dataLabels: { enabled: false },
        showInLegend: true,
        point: {
          events: {
            legendItemClick: (e) => {
              e.preventDefault()
            },
          },
        },
      },
      series: {
        innerSize: pieInnerSize * 100 + '%', // e.g. '90%'
        slicedOffset: 0,
        states: {
          hover: { halo: null },
        },

      },
    },

    legend: {
      enabled: true,
      itemDistance: 8,
      margin: 10,
      padding: 3,
      symbolPadding: 2,
      backgroundColor: '#f1f1f1',
      borderRadius: 6,
      borderColor: '#f1f1f1',
      borderWidth: 4,
      itemStyle: {
        fontSize: '10px',
        fontWeight: 'normal',
        fontFamily: 'Open Sans',
      },
    },
    credits: { enabled: false },
    title: { text: undefined },
    accessibility: { enabled: false },
  })

  useEffect(() => {
    updateSeries()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    updateSeries()
  }, [data]) // eslint-disable-line react-hooks/exhaustive-deps

  const updateSeries = () => {
    const chart = chartRef.current?.chart
    const series = data.map(entry => ({
      y: entry.value,
      name: entry.name,
      color: entry.color || '#0A91CB',
    }))

    setChartOptions({
      ...chartOptions,
      ...{
        series: [{ data: series }],
      },
    })

    const noData = (!data || (!data[0].y && !data[1].y))
    let newSeries = []
    if (noData) {
      chart?.showNoData()
    } else {
      chart?.hideNoData()
      newSeries = [{ data: data }]
    }
    setChartOptions({
      ...chartOptions,
      ...{
        series: newSeries,
      },
    })
  }

  const redrawCallback = () => {
    renderLabels()
  }

  // render central bottom arc
  const renderArc = () => {
    const chart = chartRef.current?.chart
    const renderer = chart.renderer
    const [centerX, centerY] = chart.series[0].center
    const radius = Math.min(centerX, centerY)
    const arcRadius = pieInnerSize * radius - 5
    const arcCropY = centerY + radius * 0.2

    /* eslint-disable no-multi-spaces */
    renderer.arc(
      centerX,    // x
      centerY,    // y
      arcRadius,  // radius
      0,          // inner radius
      0,          // angle start
      Math.PI     // angle end
    )
      .attr({
        fill: '#F4F5F6',
      })
      .clip(renderer.clipRect(centerX - radius, arcCropY, 2 * radius, radius)) // x, y, w, h
      .add(labelsGroupSVG.current)
  }

  const translateLabelAsync = ({ labelSVG, vertPos, centerX }) => {
    const labelBox = labelSVG.getBBox(true) // skip the cache and reload
    labelSVG.translate(centerX - labelBox.width / 2, vertPos)
  }

  const renderLabel = ({ dataPoint, isTopLabel }) => {
    const chart = chartRef.current?.chart
    const renderer = chart.renderer
    const [centerX, centerY] = chart.series[0].center
    const radius = Math.min(centerX, centerY)
    const vertPos = centerY + (isTopLabel ? -radius * 0.1 : radius * 0.65)

    const labelSVG = renderer.text(dataPoint.y, 0, 0)
      .css({
        color: dataPoint.color,
        fontSize: isTopLabel ? '28px' : '20px',
        fontFamily: 'Exo',
      })
      .add(labelsGroupSVG.current)

    const translateLabel = () => {
      translateLabelAsync({ labelSVG, vertPos, centerX })
    }

    translateLabel()

    // Re-center after the custom font is loaded, since getBBox() can use fallback font metrics initially.
    document.fonts?.load(`${isTopLabel ? 28 : 20}px Exo`).then(translateLabel)
  }

  const renderLabels = () => {
    const chart = chartRef.current?.chart
    if (!chart.series.length) {
      if (labelsGroupSVG.current) {
        labelsGroupSVG.current.destroy()
        labelsGroupSVG.current = null
      }
      return
    }

    const needFadeIn = !labelsGroupSVG.current
    if (labelsGroupSVG.current) {
      labelsGroupSVG.current.destroy()
    }

    const renderer = chart.renderer

    // create SVG group
    if (needFadeIn) {
      const fadeAnimation = chart.options.chart.animation === false ? false : { duration: 1000 }
      labelsGroupSVG.current = renderer.g()
        .attr({ opacity: 0 })
        .animate({ opacity: 1 }, fadeAnimation)
        .add()
    } else {
      labelsGroupSVG.current = renderer.g().add()
    }

    renderArc()

    // render labels
    const data = chart.series[0].data
    if (data.length === 2) {
      let [topDataPoint, bottomDataPoint] = data
      if (topDataPoint.y < bottomDataPoint.y) {
        [topDataPoint, bottomDataPoint] = [bottomDataPoint, topDataPoint]
      }

      renderLabel({ dataPoint: topDataPoint, isTopLabel: true })
      renderLabel({ dataPoint: bottomDataPoint, isTopLabel: false })
    }
  }

  return (
    <StyledHighcharts
      highcharts={Highcharts}
      containerProps={{
        style: {
          width: '100%',
          height: '100%',
        },
      }}
      options={chartOptions}
      forwardRef={chartRef}
    />
  )
}

DonutChart.propTypes = {
  data: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      color: PropTypes.string,
      y: PropTypes.number,
    })
  ),
}

export default DonutChart
